import React, { useState } from 'react';
import api from '../../utils/api';

const UTILITIES = ['water', 'electricity', 'gas', 'internet', 'trash', 'heat'];

export default function CreateListing({ onClose, existing }) {
  const [form, setForm] = useState({
    property_name: existing?.property_name || '',
    property_type: existing?.property_type || 'apartment',
    address: existing?.address || '',
    city: existing?.city || 'Iowa City',
    state: existing?.state || 'IA',
    zip: existing?.zip || '',
    monthly_rent: existing?.monthly_rent || '',
    bedrooms: existing?.bedrooms || '',
    bathrooms: existing?.bathrooms || '',
    available_date: existing?.available_date?.split('T')[0] || '',
    description: existing?.description || '',
    pet_policy: existing?.pet_policy || '',
    parking: existing?.parking || '',
    utilities_included: existing?.utilities_included || [],
    contact_email: existing?.contact_email || '',
    contact_phone: existing?.contact_phone || '',
  });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleUtility = (u) => {
    setForm(prev => ({
      ...prev,
      utilities_included: prev.utilities_included.includes(u)
        ? prev.utilities_included.filter(x => x !== u)
        : [...prev.utilities_included, u],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          data.append(k, JSON.stringify(v));
        } else if (v !== '') {
          data.append(k, v);
        }
      });
      photos.forEach(f => data.append('photos', f));

      if (existing) {
        await api.put(`/listings/${existing.id}`, data);
      } else {
        await api.post('/listings', data);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container max-w-3xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onClose} className="btn-ghost border border-[#DDDDDD]">← Back</button>
        <h1 className="section-title">{existing ? 'Edit Listing' : 'Post a Listing'}</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-red-600 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <div>
          <h2 className="font-bold text-[#1B1B1B] mb-3">What type of property is this?</h2>
          <div className="flex gap-3">
            {[
              { value: 'apartment', label: 'Apartment' },
              { value: 'house', label: 'House' },
            ].map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => setForm({ ...form, property_type: t.value })}
                className={`flex-1 py-3 rounded-xl border-2 font-semibold transition-colors ${
                  form.property_type === t.value
                    ? 'border-[#C8A800] bg-[#C8A800]/10 text-[#C8A800]'
                    : 'border-[#E0E0E0] text-[#777777] hover:border-[#C8A800]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-bold text-[#1B1B1B] mb-4">Property Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">Property Name</label>
              <input className="input" placeholder="Hawkeye Apartments #2B" value={form.property_name}
                onChange={e => setForm({ ...form, property_name: e.target.value })} required />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Street Address</label>
              <input className="input" placeholder="123 Burlington St" value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })} required />
            </div>
            <div>
              <label className="label">City</label>
              <input className="input" value={form.city}
                onChange={e => setForm({ ...form, city: e.target.value })} required />
            </div>
            <div>
              <label className="label">ZIP</label>
              <input className="input" placeholder="52240" value={form.zip}
                onChange={e => setForm({ ...form, zip: e.target.value })} />
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-bold text-[#1B1B1B] mb-4">Pricing &amp; Specs</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="label">Monthly Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]">$</span>
                <input type="number" className="input pl-7" placeholder="800" value={form.monthly_rent}
                  onChange={e => setForm({ ...form, monthly_rent: e.target.value })} required />
              </div>
            </div>
            <div>
              <label className="label">Bedrooms</label>
              <input type="number" className="input" placeholder="2" min="1" value={form.bedrooms}
                onChange={e => setForm({ ...form, bedrooms: e.target.value })} required />
            </div>
            <div>
              <label className="label">Bathrooms</label>
              <input type="number" className="input" placeholder="1" step="0.5" min="1" value={form.bathrooms}
                onChange={e => setForm({ ...form, bathrooms: e.target.value })} required />
            </div>
            <div>
              <label className="label">Available Date</label>
              <input type="date" className="input" value={form.available_date}
                onChange={e => setForm({ ...form, available_date: e.target.value })} />
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-bold text-[#1B1B1B] mb-4">Policies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Pet Policy</label>
              <select className="input" value={form.pet_policy} onChange={e => setForm({ ...form, pet_policy: e.target.value })}>
                <option value="">Select...</option>
                <option value="no_pets">No Pets</option>
                <option value="cats_only">Cats Only</option>
                <option value="dogs_allowed">Dogs Allowed</option>
                <option value="all_pets">All Pets Welcome</option>
              </select>
            </div>
            <div>
              <label className="label">Parking</label>
              <select className="input" value={form.parking} onChange={e => setForm({ ...form, parking: e.target.value })}>
                <option value="">Select...</option>
                <option value="reserved">Reserved Parking Space</option>
                <option value="driveway">Driveway</option>
                <option value="street">Street Parking</option>
                <option value="paid">Parking Costs Extra</option>
                <option value="none">No Parking Available</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="label">Utilities Included</label>
            <div className="flex flex-wrap gap-2">
              {UTILITIES.map(u => (
                <button key={u} type="button" onClick={() => toggleUtility(u)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize border transition-colors ${
                    form.utilities_included.includes(u)
                      ? 'border-[#C8A800] bg-[#C8A800]/10 text-[#C8A800]'
                      : 'border-[#DDDDDD] text-[#555555] hover:border-[#BBBBBB]'
                  }`}>
                  {u}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="label">Description</label>
          <textarea className="input h-28 resize-none" placeholder="Describe your property..." value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>

        <div>
          <h2 className="font-bold text-[#1B1B1B] mb-4">Photos</h2>
          <input type="file" accept="image/*" multiple className="input" onChange={e => setPhotos(Array.from(e.target.files))} />
          {photos.length > 0 && <p className="nav-link text-sm text-[#777777] mt-1">{photos.length} photo(s) selected</p>}
        </div>

        <div>
          <h2 className="font-bold text-[#1B1B1B] mb-4">Contact Info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Contact Email</label>
              <input type="email" className="input" value={form.contact_email}
                onChange={e => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <label className="label">Contact Phone</label>
              <input type="tel" className="input" placeholder="(319) 555-0100" value={form.contact_phone}
                onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost flex-1 border border-[#DDDDDD]">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary flex-1">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : existing ? 'Save Changes' : 'Post Listing'}
          </button>
        </div>
      </form>
    </div>
  );
}
